/**
 * AnimatedScanReveal — Orange Optimise diagnostic results reveal system.
 *
 * DISPLAY-ONLY animation of already-completed backend scan data. Never
 * generates, mutates or delays the actual score.
 *
 * Exports (all named):
 *   - useReducedMotion()         → boolean
 *   - useCountUp(target, opts)   → animated number, honours reduced-motion
 *   - AnalysingLabels            → transitional "ANALYSING PERFORMANCE…" strip
 *   - OrangeScoreReveal          → big number + ring, count-up + status fade
 *   - CategoryBar                → single category card (count-up + width anim)
 *   - CategoriesGrid             → staggered grid of CategoryBar
 *   - PrioritiesReveal           → staggered fade-up list of top priorities
 *
 * ACCESSIBILITY:
 *   - `prefers-reduced-motion` snaps to final values immediately.
 *   - Real numeric values always live in the DOM so screen readers get them.
 *
 * PERFORMANCE:
 *   - Count-ups use requestAnimationFrame with a cubic ease-out; a single
 *     rAF per number keeps re-renders cheap (<60 renders per number total).
 *   - Framer-motion (already in the bundle) handles the fade/slide entrance.
 */
import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// ------------------------------------------------------------- theme helpers
const BAND_COLOR = {
  good: "#0FA958",
  needs_attention: "#E8A03B",
  poor: "#C1350B",
};

const CATEGORY_LABEL = {
  performance: "Performance",
  mobile: "Mobile",
  assets: "Assets",
  technical: "Technical",
  security: "Security Health",
  efficiency: "Efficiency",
};

const CATEGORY_TOOLTIP = {
  performance: "Speed and rendering — Core Web Vitals, TTFB, load time.",
  mobile: "Mobile experience — responsive layout, viewport, tap targets.",
  assets: "Images, fonts and static assets — size, format, compression.",
  technical: "HTML/HTTP setup — status codes, redirects, headers, SEO basics.",
  security: "Security headers — HTTPS, HSTS, CSP, X-Frame-Options, etc.",
  efficiency: "Cache, compression, third-party scripts and overall footprint.",
};

const ANALYSING_MSGS = [
  "ANALYSING PERFORMANCE…",
  "CHECKING MOBILE EXPERIENCE…",
  "INSPECTING ASSETS…",
  "REVIEWING TECHNICAL SETUP…",
  "CHECKING SECURITY HEALTH…",
  "MEASURING EFFICIENCY…",
];

// ------------------------------------------------------------- reduced motion
export function useReducedMotion() {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    try { return !!window.matchMedia("(prefers-reduced-motion: reduce)").matches; }
    catch { return false; }
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduced(!!mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return reduced;
}

// ---------------------------------------------------------- count-up hook
export function useCountUp(target, { duration = 1200, active = true, delay = 0 } = {}) {
  const reduced = useReducedMotion();
  const [value, setValue] = useState(active && !reduced ? 0 : Number(target || 0));
  const rafRef = useRef();
  const timeoutRef = useRef();

  useEffect(() => {
    if (!active) return;
    if (reduced) { setValue(Number(target || 0)); return; }
    const goal = Number(target || 0);
    setValue(0);
    const kick = () => {
      const start = performance.now();
      const step = (now) => {
        const p = Math.min(1, (now - start) / duration);
        // Cubic ease-out
        const eased = 1 - Math.pow(1 - p, 3);
        setValue(Math.round(goal * eased));
        if (p < 1) rafRef.current = requestAnimationFrame(step);
        else setValue(goal); // lock final
      };
      rafRef.current = requestAnimationFrame(step);
    };
    if (delay > 0) timeoutRef.current = setTimeout(kick, delay);
    else kick();
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, active, reduced, duration, delay]);

  return value;
}

// ---------------------------------------------------------- Analysing labels
export function AnalysingLabels({ active, testId }) {
  const [idx, setIdx] = useState(0);
  const reduced = useReducedMotion();
  useEffect(() => {
    if (!active || reduced) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % ANALYSING_MSGS.length), 900);
    return () => clearInterval(t);
  }, [active, reduced]);
  if (!active) return null;
  return (
    <div className="text-center" data-testid={testId || "optimise-analysing"}>
      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.25 }}
          className="inline-block font-black text-xl md:text-2xl tracking-[0.14em] uppercase"
        >
          {ANALYSING_MSGS[idx]}
        </motion.div>
      </AnimatePresence>
      <div className="mt-3 text-[11px] tracking-[0.28em] uppercase font-bold text-black/60">
        Compiling your Orange Score…
      </div>
    </div>
  );
}

// ---------------------------------------------------------- Score ring SVG
function ScoreRing({ progressPct, size = 132, stroke = 8, color = "#0A0A0A", track = "#FFE0BF" }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, progressPct));
  const offset = c - (clamped / 100) * c;
  return (
    <svg width={size} height={size} aria-hidden style={{ display: "block" }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={stroke}/>
      <circle
        cx={size / 2} cy={size / 2} r={r}
        fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={c} strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: "stroke-dashoffset 200ms linear" }}
      />
    </svg>
  );
}

// ---------------------------------------------------------- Orange Score reveal
export function OrangeScoreReveal({
  score, band, statusLabel, subtitle, revealKey,
  size = "large", testId, showRing = true,
}) {
  const active = revealKey != null;
  const shown = useCountUp(score, { duration: 1500, active });
  const reduced = useReducedMotion();
  const finalReached = shown >= Number(score || 0);
  const color = BAND_COLOR[band] || "#0A0A0A";

  const numberClass =
    size === "hero"  ? "font-black text-7xl md:text-8xl tracking-[-0.03em]" :
    size === "small" ? "font-black text-4xl tracking-[-0.02em]" :
                       "font-black text-6xl tracking-[-0.03em]";

  return (
    <div className="flex items-start gap-6" data-testid={testId || "optimise-orange-score-reveal"}>
      {showRing && size !== "small" && (
        <div className="relative shrink-0" style={{ width: size === "hero" ? 148 : 132, height: size === "hero" ? 148 : 132 }}>
          <ScoreRing
            progressPct={Number(score || 0) * (Math.min(1, (shown / Math.max(1, Number(score || 1)))))}
            size={size === "hero" ? 148 : 132}
            stroke={size === "hero" ? 9 : 8}
            color={color}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className={`font-black tracking-[-0.02em] ${size === "hero" ? "text-4xl" : "text-3xl"}`} aria-live="polite">
              {shown}
            </div>
          </div>
        </div>
      )}
      <div className="min-w-0">
        <div className="text-[11px] tracking-[0.32em] uppercase font-bold text-[#FF6A00]">Orange Score</div>
        <div className="mt-1 flex items-baseline gap-3">
          <div className={numberClass} aria-label={`Orange Score ${score} out of 100`}>
            <span aria-hidden>{shown}</span>
          </div>
          <div className="font-black text-2xl text-[#8A8A8A]">/100</div>
        </div>
        <AnimatePresence>
          {(finalReached || reduced) && statusLabel && (
            <motion.div
              key="statusLabel"
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              className="mt-2 font-black text-lg tracking-[0.05em]"
              style={{ color }}
              data-testid="optimise-orange-score-status"
            >
              {statusLabel}
            </motion.div>
          )}
        </AnimatePresence>
        {subtitle && <div className="mt-1 text-sm text-black/70">{subtitle}</div>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------- Category card
function CategoryBar({ name, cat, delay, testId, revealKey }) {
  const score = Number(cat?.score || 0);
  const band = cat?.band || "needs_attention";
  const color = BAND_COLOR[band] || "#4A4A4A";
  const active = revealKey != null;
  const reduced = useReducedMotion();
  const shown = useCountUp(score, { duration: 900, active, delay });
  const [barMounted, setBarMounted] = useState(reduced);
  useEffect(() => {
    if (reduced) { setBarMounted(true); return; }
    const t = setTimeout(() => setBarMounted(true), delay);
    return () => clearTimeout(t);
  }, [delay, reduced, revealKey]);

  return (
    <motion.div
      className="group bg-white border border-[#EAEAEA] rounded-2xl p-5 hover:border-[#FF6A00] transition-colors relative"
      data-testid={testId}
      title={CATEGORY_TOOLTIP[name] || ""}
      initial={reduced ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: reduced ? 0 : 0.45, delay: reduced ? 0 : delay / 1000 }}
    >
      <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">
        {CATEGORY_LABEL[name] || name}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className="font-black text-3xl tracking-[-0.02em]" aria-label={`${name} ${score}`}>
          <span aria-hidden>{shown}</span>
        </div>
        <div
          className="text-[11px] tracking-[0.2em] uppercase font-bold"
          style={{ color, opacity: shown >= score ? 1 : 0.5 }}
        >
          {String(band).replace("_", " ")}
        </div>
      </div>
      <div
        className="mt-2 h-1.5 rounded-full bg-[#F6F5F2] overflow-hidden"
        role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={score}
      >
        <div
          className="h-full rounded-full"
          style={{
            width: reduced ? `${score}%` : (barMounted ? `${score}%` : "0%"),
            background: color,
            transition: reduced ? "none" : "width 900ms cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        />
      </div>
      <div className="pointer-events-none absolute inset-x-3 -bottom-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="mx-auto max-w-full bg-black text-white text-[11px] leading-snug rounded-lg px-3 py-2 shadow-lg">
          {CATEGORY_TOOLTIP[name] || ""}
        </div>
      </div>
    </motion.div>
  );
}

export function CategoriesGrid({ categories, revealKey, columns = "grid-cols-2 md:grid-cols-3", testIdPrefix = "optimise-cat", staggerMs = 100, startDelay = 400 }) {
  const entries = Object.entries(categories || {});
  return (
    <div className={`grid ${columns} gap-3`} data-testid="optimise-categories-grid">
      {entries.map(([k, v], i) => (
        <CategoryBar
          key={`${revealKey}-${k}`}
          name={k}
          cat={typeof v === "object" ? v : { score: v, band: v >= 80 ? "good" : v >= 50 ? "needs_attention" : "poor" }}
          delay={startDelay + i * staggerMs}
          testId={`${testIdPrefix}-${k}`}
          revealKey={revealKey}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------- Priorities reveal
export function PrioritiesReveal({ items, revealKey, testId, empty = "Nothing critical — nice work. 🍊", startDelay = 1400, staggerMs = 100 }) {
  const reduced = useReducedMotion();
  if (!items || items.length === 0) {
    return <li className="text-sm text-black/60" data-testid={`${testId || "priorities"}-empty`}>{empty}</li>;
  }
  return (
    <>
      {items.map((f, i) => (
        <motion.li
          key={`${revealKey}-${i}`}
          initial={reduced ? false : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reduced ? 0 : 0.42, delay: reduced ? 0 : (startDelay + i * staggerMs) / 1000 }}
          className="flex items-start gap-3 p-4 rounded-2xl bg-[#FFF7EE] border border-[#FCE1C7]"
          data-testid={`${testId || "priorities"}-${i}`}
        >
          <span
            className="mt-1 w-2 h-2 rounded-full shrink-0"
            style={{ background: f.severity === "critical" ? "#C1350B" : "#E8A03B" }}
          />
          <div>
            <div className="font-black text-[15px]">{f.title || f.name || "Issue"}</div>
            {(f.message || f.description) && (
              <div className="text-sm text-black/80 mt-1">{f.message || f.description}</div>
            )}
          </div>
        </motion.li>
      ))}
    </>
  );
}

export { BAND_COLOR, CATEGORY_LABEL };
